import { useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { ApiACLRole, ApiAIImageGenerationBackend } from "@incendium/api";
import { generateImage } from "features/ai/services/aiService";
import { useSelectedProject } from "Hooks/useSelectedProject";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import AccessLevel from "Components/AccessLevel/AccessLevel";

const SIZE_OPTIONS = [
  { value: "1k", label: "medium" },
  { value: "2k", label: "large" },
];

const ASPECT_RATIO_OPTIONS = [
  "1:1",
  "16:9",
  "9:16",
  "4:3",
  "3:4",
  "3:2",
  "2:3",
];

const BACKEND_OPTIONS = [
  {
    value: ApiAIImageGenerationBackend.AI_IMAGE_GENERATION_BACKEND_GEMINI_CONTEXT,
    label: "Gemini Context",
  },
  {
    value: ApiAIImageGenerationBackend.AI_IMAGE_GENERATION_BACKEND_GEMINI_GEN,
    label: "Gemini Gen",
  },
];

interface AIImageBlockDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (src: string) => void;
}

export default function AIImageBlockDialog({
  open,
  onClose,
  onGenerated,
}: AIImageBlockDialogProps) {
  const { selectedProject } = useSelectedProject();
  const projectId = selectedProject?.id;
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState(SIZE_OPTIONS[0].value);
  const [aspectRatio, setAspectRatio] = useState(ASPECT_RATIO_OPTIONS[0]);
  const [backend, setBackend] = useState<ApiAIImageGenerationBackend>(
    ApiAIImageGenerationBackend.AI_IMAGE_GENERATION_BACKEND_GEMINI_CONTEXT
  );
  const [generating, setGenerating] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const theme = useTheme();

  const handleGenerate = useCallback(async () => {
    if (!projectId || !prompt.trim()) return;
    setGenerating(true);
    setPreviewSrc(null);
    try {
      const res = await generateImage(projectId, {
        prompt: prompt.trim(),
        backend,
        size,
        aspectRatio,
      });
      if (res) {
        setPreviewSrc(`data:image/png;base64,${res}`);
        setPrompt("");
        setBackend(ApiAIImageGenerationBackend.AI_IMAGE_GENERATION_BACKEND_GEMINI_CONTEXT);
      }
    } finally {
      setGenerating(false);
    }
  }, [projectId, prompt, backend, size, aspectRatio]);

  const handleUseImage = useCallback(() => {
    if (previewSrc) {
      onGenerated(previewSrc);
      onClose();
    }
  }, [previewSrc, onGenerated, onClose]);

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate AI Image</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Generate creates a new image. To edit an existing image, use the edit option.
        </Alert>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            mt: 1,
            minHeight: 320,
          }}
        >
          <Box
            sx={{
              flex: "1 1 50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === "light" ? "grey.50" : "grey.900",
              overflow: "hidden",
            }}
          >
            {previewSrc ? (
              <Box
                component="img"
                src={previewSrc}
                alt="Generated preview"
                sx={{
                  maxWidth: "100%",
                  maxHeight: 320,
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Preview will appear here
              </Typography>
            )}
          </Box>
          <Stack spacing={2} sx={{ flex: "1 1 50%", minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              label="Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              multiline
              minRows={2}
            />
            <TextField
              fullWidth
              size="small"
              label="Size"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              select
            >
              {SIZE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
           <TextField
            fullWidth
            size="small"
            label="Aspect Ratio"
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            select
           >
            {ASPECT_RATIO_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
           </TextField>
           
            <AccessLevel role={ApiACLRole.PROJECT_ADMIN} >
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Model
                </Typography>
                <ToggleButtonGroup
                  fullWidth
                  size="small"
                  color="primary"
                  exclusive
                  value={backend}
                  onChange={(_, v) =>
                    v !== null && setBackend(v as ApiAIImageGenerationBackend)
                  }
                >
                  {BACKEND_OPTIONS.map((o) => (
                    <ToggleButton key={o.value} value={o.value}>
                      {o.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </AccessLevel>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!prompt.trim() || !projectId || generating}
            >
              {generating ? "Generating…" : "Generate"}
            </Button>
            {previewSrc && (
              <Button variant="outlined" onClick={handleUseImage}>
                Use this image
              </Button>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </CustomDialog>
  );
}
